import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getAnalyticsPageName} from 'modules/meta/selectors'
import {getCallToBook, getIsCallToBookAvailable} from 'modules/search/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Link} from '@vio/atlas/Link'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/src/themes'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {getEventPayload} from './useCallToBook'

const LinkWrapper = styled(Link)`
  text-decoration: none;
  &:active,
  &:focus {
    text-decoration: none;
  }
`

const APWrapper = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: ${cssTheme.layout.spacing.s300};
  padding-top: ${cssTheme.layout.spacing.s250};
  padding-bottom: ${cssTheme.layout.spacing.s250};
  padding-right: ${cssTheme.layout.spacing.s200};
  padding-left: ${cssTheme.layout.spacing.s200};
  color: ${cssTheme.colors.content.neutral.primary};
  background-color: ${cssTheme.colors.background.accent.tertiary};
  border-top-left-radius: ${cssTheme.layout.radius.md};
  border-top-right-radius: ${cssTheme.layout.radius.md};
`

const APStickyWrapper = styled(APWrapper)`
  width: auto;
  padding-top: ${cssTheme.layout.spacing.s250};
  padding-bottom: ${cssTheme.layout.spacing.s250};
  padding-right: ${cssTheme.layout.spacing.s400};
  padding-left: ${cssTheme.layout.spacing.s400};
  box-shadow:
    0 0 2px rgba(39, 36, 41, 0.1),
    0 -2px 8px -4px rgba(39, 36, 41, 0.1);
`

// B side of 90ef358d-ap-first-fold-improvement-v2
const FirstFoldLinkWrapper = styled(LinkWrapper)`
  display: block;
`

const FirstFoldWrapper = styled.div<{isSticky?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${cssTheme.layout.spacing.s200};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400};
  color: ${cssTheme.colors.content.accent.primary};
  background-color: ${cssTheme.colors.background.accent.secondary};
  ${({isSticky}) => !isSticky && `border-radius: ${cssTheme.layout.radius.md};`}
`

const FirstFoldPhoneNumber = styled.span`
  font-weight: ${cssTheme.fonts.weight.lg};
  text-decoration: underline;
`

export const CALL_TO_BOOK_VARIANTS = ['AP', 'AP_STICKY'] as const

type CallToBookVariant = (typeof CALL_TO_BOOK_VARIANTS)[number]

interface Props {
  sourceComponent?: string
  variant?: CallToBookVariant
}

const APContent = ({phoneNumber}: {phoneNumber: string}) => (
  <>
    <Box display="flex" alignItems="center" gap="s100">
      <Icon name="Phone" size="sm" />
      <Text variant="labelS">
        <FormattedMessageWrapper
          id="callToBook.callForGreatDeals"
          defaultMessage="Call for great deals"
        />
      </Text>
    </Box>
    <Link size="sm">{phoneNumber}</Link>
  </>
)

export const CallToBook = ({sourceComponent = '', variant = 'AP'}: Props) => {
  const callToBook = useSelector(getCallToBook)
  const {phoneNumber, displayedPhoneNumber, href, payload} = callToBook || {}
  const isCallToBookAvailable = useSelector(getIsCallToBookAvailable)

  const pageName = useSelector(getAnalyticsPageName)
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()
  const toggle = useToggle()

  const handleOnClick = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CallToBook,
        action: Action.Clicked,
        page: pageName,
        payload: getEventPayload({
          payload,
          displayedPhoneNumber,
          component: sourceComponent
        })
      })
    )
  }, [dispatch, pageName, payload, displayedPhoneNumber, sourceComponent])

  if (!isCallToBookAvailable) return null

  // The first-fold experiment only runs on the mobile AP, so keep desktop out of
  // the decision to avoid activating it for traffic that never sees the B side.
  if (
    isMobile &&
    toggle('90ef358d-ap-first-fold-improvement-v2', false, true)
  ) {
    return (
      <FirstFoldLinkWrapper onClick={handleOnClick} href={href}>
        <FirstFoldWrapper isSticky={variant === 'AP_STICKY'}>
          <Icon name="Phone" size="sm" />
          <Text variant="labelS">
            <FormattedMessageWrapper
              id="callToBook.bookByPhone"
              defaultMessage="Book by phone: {phoneNumber}"
              values={{
                phoneNumber: (
                  <FirstFoldPhoneNumber>{phoneNumber}</FirstFoldPhoneNumber>
                )
              }}
            />
          </Text>
        </FirstFoldWrapper>
      </FirstFoldLinkWrapper>
    )
  }

  if (variant === 'AP') {
    return (
      <LinkWrapper onClick={handleOnClick} href={href}>
        <APWrapper>
          <APContent phoneNumber={phoneNumber} />
        </APWrapper>
      </LinkWrapper>
    )
  }

  if (variant === 'AP_STICKY') {
    return (
      <LinkWrapper onClick={handleOnClick} href={href}>
        <APStickyWrapper>
          <APContent phoneNumber={phoneNumber} />
        </APStickyWrapper>
        <Divider />
      </LinkWrapper>
    )
  }

  return null
}
